import {BASE_URL_PLACEHOLDER} from '@/utils/constants/apiEndPoints'
import {FeatureConfig, FeatureToggle} from '@/utils/constants/featuresFlag'
import {getCountries} from '@/helpers/envConfig'
import {isFeatureEnabled} from '@/helpers/featureToggle'
import axios, {AxiosError, HttpStatusCode} from 'axios'
import {ROUTES} from '@/utils/constants/routesConstants'
import refreshToken from '@/helpers/Auth/refreshToken'

export const HTTPMethod = {
  DELETE: 'DELETE',
  GET: 'GET',
  POST: 'POST',
  PATCH: 'PATCH',
  PUT: 'PUT',
}

export const ContentType = {
  APPLICATION_JSON: 'application/json',
  MULTIPART_FORMDATA: 'multipart/form-data',
}

let RefreshRetryCount = 0
const onUnauthorisedError = async (error) => {
  if (RefreshRetryCount >= 3) {
    localStorage.removeItem('msalAccessToken')
    window.location.href = ROUTES.SESSION_EXPIRED
    return
  }

  const newToken = await refreshToken()
  if (newToken) {
    RefreshRetryCount++
    // Retry the original request with the new token
    if (error.config) {
      error.config.headers['Authorization'] = `Bearer ${newToken}`
      return axiosiInstance.request(error.config)
    }
  } else {
    window.location.href = ROUTES.SESSION_EXPIRED
  }
}

export const handleNetworkErrors = async error => {
  console.log('Network error:', error.code, error.response?.status)
  const isCanceled = error.code === AxiosError.ERR_CANCELED

  if (isCanceled) return Promise.reject(error)

  const isUnauthorized = error.response?.status === HttpStatusCode.Unauthorized
  const isForbidden = error.response?.status === HttpStatusCode.Forbidden
  const isNetworkError = error.code === AxiosError.ERR_NETWORK

  if (isForbidden) {
    console.log('Forbidden error, redirecting to 403 page')
    window.location.href = ROUTES.ERROR_403
  } else if (isUnauthorized || isNetworkError) {
    console.log('Unauthorized or network error, handling logout')
    try {
      await onUnauthorisedError()
    } catch (signOutError) {
      console.error('onUnauthorisedError::ERROR', signOutError)
    }
    // window.location.reload();
  }

  return Promise.reject(error)
}

const axiosiInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
})
axiosiInstance.interceptors.request.use(
  config => {
    console.log('Request interceptor:', config.method, config.url)
    return config
  },
  error => {
    console.log('Request interceptor error')
    return handleNetworkErrors(error)
  }
)
axiosiInstance.interceptors.response.use(
  response => {
    console.log('Response interceptor:', response.status)
    return response
  },
  error => {
    console.log('Response interceptor error')
    return handleNetworkErrors(error)
  }
)

export default function api({
  method = HTTPMethod.GET,
  path,
  requestSchema,
  responseSchema,
  signal,
}) {
  return (requestData) => {
    try {
      requestSchema.parse(requestData)
    } catch (error) {
      console.error(`requestSchema.parse::ERROR [${method}] [${path}]`, error.message)
      throw error
    }

    async function apiCall() {
      const {url, baseURL} = pathAdapter(path)
      const token = localStorage.getItem('msalAccessToken')

      const response = await axiosiInstance.request({
        signal,
        baseURL,
        url,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        method,
        [method === HTTPMethod.GET ? 'params' : 'data']: requestData,
      })

      try {
        return responseSchema.parse(response.data)
      } catch (error) {
        console.error(`responseSchema.parse::ERROR [${method}] [${path}]`, error.message, response.data)
        throw error
      }
    }

    return apiCall()
  }
}

const pathAdapter = path => {
  console.log('Path adapter called:', path)
  let baseURL = import.meta.env[FeatureConfig.apiUrl]

  const adminPortalPlaceholder = BASE_URL_PLACEHOLDER.ADMIN_PORTAL
  const isAdminPortalUrl = path.includes(adminPortalPlaceholder)
  if (isAdminPortalUrl) {
    console.log('Using admin portal URL')
    baseURL = import.meta.env[FeatureConfig.apiAdminUrl]
  }

  const realTimeEventsPlaceholder = BASE_URL_PLACEHOLDER.EVENTS
  const isRealTimeEventsUrl = path.includes(realTimeEventsPlaceholder)
  if (isRealTimeEventsUrl) {
    console.log('Using real-time events URL')
    baseURL = import.meta.env[FeatureConfig.apiEventsUrl]
  }

  const url = path.replace(adminPortalPlaceholder, '').replace(realTimeEventsPlaceholder, '')
  console.log('Adapted URL:', baseURL, url)

  return {
    url,
    baseURL,
    isAdminPortalUrl,
    isRealTimeEventsUrl,
  }
}
